import { list } from 'src/app/common/classes/request-builder';
import { StorageManager } from 'src/app/common/storage-manager.class';
import { environment } from 'src/environments/environment';
import { FincasModel } from 'src/app/models/form-common/fincas.module';
import { ProductosModel } from 'src/app/models/productos/productos.model';
import { FitoLocalesModel } from 'src/app/models/tareas/fito-locales.model';
import { DropdownData, InputType } from 'src/app/common/components/reactive-form-builder/interfaces/input.interface';
import {
    Form,
    FormField,
    FormFieldConditions,
    FormSection,
    FormType,
} from 'src/app/common/components/reactive-form-builder/interfaces/form.interface';

export class FitoLocalesFormDefinition {
    public readonly hasClients = environment.features.hasClients;
    public readonly clienteName = environment.features.clienteName;
    public readonly fincaName = environment.features.fincaName;
    public readonly userCanSeeClientes = environment.features.userCanSeeClientes || StorageManager.getUser().rol === 'admin';

    public formType: FormType = FormType.CREATION;
    public model: Partial<FitoLocalesModel> = {};
    public formFields!: Form<FitoLocalesModel>;

    public fincas: DropdownData<FincasModel> = list<FincasModel>(['...', null]);
    public productos: DropdownData<ProductosModel> = list<ProductosModel>(['...', null]);
    public clientes = list();

    private readonly client = StorageManager.getClient();
    private readonly user = StorageManager.getUser();

    constructor(type: FormType, model: Partial<FitoLocalesModel>) {
        this.formType = +type;
        this.model = model;

        this.formFields = {
            name: 'fito-locales',
            header: 'Datos de la tarea fitosanitaria en locales',
            type: this.formType,
            sections: this.getFormSections(),
            table: null,
            uploadButton: {
                isEnabled: false,
                label: '',
            },
            signed: false,
        };
    }

    private getFormSections(): FormSection<FitoLocalesModel>[] {
        const sections: FormSection<FitoLocalesModel>[] = [
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getMainFields(),
                order: 1,
            },
            {
                name: '',
                width: 12,
                isVisible: true,
                fields: this.getHiddenFields(),
                order: 2,
            },
        ];

        return sections.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getMainFields(): FormField<FitoLocalesModel>[] {
        const fields: FormField<FitoLocalesModel>[] = [
            {
                name: 'id_cliente',
                vinculatedFields: undefined,
                label: this.clienteName,
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.clientes,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                    },
                },
                width: 4,
                conditions: this.conditions({
                    isRequired: true,
                    isVisible: this.hasClients && this.userCanSeeClientes && this.formType === FormType.DUPLICATION,
                }),
                order: 1,
            },
            {
                name: 'id_finca',
                vinculatedFields: ['id_cliente'],
                label: this.fincaName,
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.fincas,
                    defaultValue: this.model.id_finca ?? null,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                        filter: (it: { value: unknown }): boolean => this.filterFinca(it),
                    },
                },
                width: 4,
                conditions: this.conditions({ isRequired: true }),
                order: 2,
            },
            {
                name: 'fecha',
                vinculatedFields: undefined,
                label: 'Fecha',
                input: {
                    type: InputType.CALENDAR,
                    defaultValue: this.model.fecha ?? (this.formType === FormType.CREATION ? new Date() : null),
                    isEditable: true,
                    calendarConfiguration: {
                        calendarView: 'date',
                        calendarDateFormat: 'dd/mm/yy',
                    },
                },
                width: 4,
                conditions: this.conditions({ isRequired: true }),
                order: 3,
            },
            {
                name: 'id_producto',
                vinculatedFields: undefined,
                label: 'Producto Fito',
                input: {
                    type: InputType.DROPDOWN_SEARCH,
                    data: this.productos,
                    defaultValue: (this.model as Record<string, unknown>)['id_producto'] ?? null,
                    isEditable: true,
                    dropdownConfiguration: {
                        valuePrimaryKey: 'id',
                    },
                },
                width: 4,
                conditions: this.conditions(),
                order: 4,
            },
            {
                name: 'local',
                vinculatedFields: undefined,
                label: 'Local',
                input: {
                    type: InputType.TEXT,
                    defaultValue: (this.model as Record<string, unknown>)['local'] ?? '',
                    isEditable: true,
                },
                width: 4,
                conditions: this.conditions(),
                order: 5,
            },
            {
                name: 'superficie_tratada',
                vinculatedFields: undefined,
                label: 'Superficie tratada',
                input: {
                    type: InputType.NUMERIC,
                    defaultValue: (this.model as Record<string, unknown>)['superficie_tratada'] ?? '',
                    isEditable: true,
                },
                width: 4,
                conditions: this.conditions(),
                order: 6,
            },
            {
                name: 'motivo',
                vinculatedFields: undefined,
                label: 'Motivo fitosanitario',
                input: {
                    type: InputType.TEXT,
                    defaultValue: (this.model as Record<string, unknown>)['motivo'] ?? '',
                    isEditable: true,
                },
                width: 4,
                conditions: this.conditions(),
                order: 7,
            },
        ];

        return fields.sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    }

    private getHiddenFields(): FormField<FitoLocalesModel>[] {
        return [
            {
                name: 'id',
                vinculatedFields: undefined,
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.model.id ?? null,
                    isEditable: false,
                },
                width: 1,
                conditions: this.conditions({ isVisible: true, isRememberedOnDuplicate: false }),
                order: 1,
            },
            {
                name: 'id_cliente',
                vinculatedFields: ['id_finca'],
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.model.id_cliente ?? this.client?.id ?? null,
                    isEditable: false,
                },
                width: 1,
                updateValue: (): string => this.getPayloadClientId(),
                conditions: this.conditions({
                    isVisible: !(this.hasClients && this.userCanSeeClientes && this.formType === FormType.DUPLICATION),
                }),
                order: 2,
            },
            {
                name: 'id_usuario',
                vinculatedFields: ['id_finca'],
                label: '',
                input: {
                    type: InputType.HIDDEN,
                    defaultValue: this.model.id_usuario ?? this.user?.id ?? null,
                    isEditable: false,
                },
                width: 1,
                updateValue: (): string => this.getPayloadUserId(),
                conditions: this.conditions({ isVisible: true }),
                order: 3,
            },
        ];
    }

    private getPayloadClientId(formValue?: unknown): string {
        const selectedFinca = this.fincas.selected as FincasModel | null | undefined;
        const selectedClient = this.clientes.selected as { id?: string | number } | null | undefined;

        return String(
            selectedFinca?.id_cliente ??
            formValue ??
            this.model.id_cliente ??
            selectedClient?.id ??
            this.client?.id ??
            '',
        );
    }

    private getPayloadUserId(formValue?: unknown): string {
        const selectedFinca = this.fincas.selected as FincasModel | null | undefined;

        return String(
            selectedFinca?.id_usuario ??
            formValue ??
            this.model.id_usuario ??
            this.user?.id ??
            '',
        );
    }

    private filterFinca(it: { value: unknown }): boolean {
        const finca = it.value as FincasModel | null;

        if (!finca || !this.hasClients || this.formType === FormType.DUPLICATION) {
            return true;
        }

        if (this.client?.id) {
            return String(finca.id_cliente ?? '') === String(this.client.id);
        }

        const selectedClient = this.clientes.selected as { id?: string | number } | null | undefined;

        if (selectedClient?.id) {
            return String(finca.id_cliente ?? '') === String(selectedClient.id);
        }

        return true;
    }

    private conditions(overrides: Partial<FormFieldConditions> = {}): FormFieldConditions {
        return {
            isRememberedOnDuplicate: true,
            isRequired: false,
            isRetained: false,
            hasUndoEnabled: false,
            isVisible: true,
            ...overrides,
        };
    }
}
